import { UNSAFE_createBrowserHistory, parsePath } from 'react-router';
import type { To } from 'react-router';

/**
 * En un subdominio de empresa (`app.agrovid.solutions`) la empresa sale del host, así que la
 * URL no lleva el segmento `/<empresa>`: el navegador muestra `/login` o `/dashboard`.
 * Por dentro las rutas siguen siendo `/:tenantId/...`, así que este adaptador de historial
 * traduce en los dos sentidos:
 *
 *   navegador /login        → router /app/login
 *   router    /app/usuarios → navegador /usuarios
 *
 * Rutas públicas sin empresa (/register, /legal, /accept-invite, /account-inactive, /demo)
 * pasan sin cambios, y las URL viejas con el código (`/app/dashboard`) siguen funcionando.
 */

type BaseHistory = ReturnType<typeof UNSAFE_createBrowserHistory>;

const PASS_THROUGH = ['/register', '/accept-invite', '/legal', '/account-inactive', '/demo'];

function isPassThrough(path: string): boolean {
  return PASS_THROUGH.some((r) => path === r || path.startsWith(`${r}/`));
}

/** Ruta del navegador → ruta que ve el router. */
export function toInnerPath(outer: string, code: string): string {
  if (outer === '/' || isPassThrough(outer)) return outer;
  if (outer === `/${code}` || outer.startsWith(`/${code}/`)) return outer; // URL vieja con el código
  return `/${code}${outer}`;
}

/** Ruta del router → ruta que se muestra en el navegador. */
export function toOuterPath(inner: string, code: string): string {
  if (inner === `/${code}`) return '/';
  if (inner.startsWith(`/${code}/`)) return inner.slice(code.length + 1);
  return inner;
}

export function createTenantHostHistory(
  code: string,
  base: BaseHistory = UNSAFE_createBrowserHistory({ v5Compat: true }),
): BaseHistory {
  // Misma ubicación base → mismo objeto interno (el router compara por referencia/clave).
  let cachedBase: BaseHistory['location'] | null = null;
  let cachedInner: BaseHistory['location'] | null = null;
  const toInnerLocation = (loc: BaseHistory['location']): BaseHistory['location'] => {
    if (loc === cachedBase && cachedInner) return cachedInner;
    cachedBase = loc;
    cachedInner = { ...loc, pathname: toInnerPath(loc.pathname, code) };
    return cachedInner;
  };

  const toOuter = (to: To): To => {
    const parsed = typeof to === 'string' ? parsePath(to) : to;
    if (parsed.pathname === undefined) return parsed;
    return { ...parsed, pathname: toOuterPath(parsed.pathname, code) };
  };

  return {
    get action() {
      return base.action;
    },
    get location() {
      return toInnerLocation(base.location);
    },
    createHref: (to) => base.createHref(toOuter(to)),
    createURL: (to) => base.createURL(toOuter(to)),
    encodeLocation: (to) => {
      const encoded = base.encodeLocation(toOuter(to));
      return { ...encoded, pathname: toInnerPath(encoded.pathname, code) };
    },
    push: (to, state) => base.push(toOuter(to), state),
    replace: (to, state) => base.replace(toOuter(to), state),
    go: (delta) => base.go(delta),
    listen: (listener) =>
      base.listen((update) => listener({ ...update, location: toInnerLocation(update.location) })),
  } as BaseHistory;
}

/** URL (ruta + query + hash) del router → la que se muestra en el navegador. */
export function toOuterUrl(url: string, code: string): string {
  const parsed = parsePath(url);
  return `${toOuterPath(parsed.pathname ?? '/', code)}${parsed.search ?? ''}${parsed.hash ?? ''}`;
}
